import { useEffect, MutableRefObject } from 'react';
import { FeatureLayer, WebMap } from '../constructors';
import { SimpleMemoryCache } from '../../common/caches/simple-memory-cache';
import { clearEntityCache } from '../helpers/entity-id-to-object-id-maps';
import { logFeatureLayerWarning } from '../helpers/utils';

type CleanupStrategy = 'reset-all' | 'filter-stale';

type UseFeatureLayerCleanupProps<T> = {
    layer?: FeatureLayer;
    webMap?: WebMap;
    cacheKey: string;
    entityIdToObjectIdMapCacheRef: MutableRefObject<SimpleMemoryCache<number>>;
    currentItems?: T[];
    cleanupStrategy?: CleanupStrategy;
    getCurrentEntityIds?: (items: T[]) => number[];
};

export function useFeatureLayerCleanup<T>({
    layer,
    webMap,
    cacheKey,
    entityIdToObjectIdMapCacheRef,
    currentItems = [],
    cleanupStrategy = 'filter-stale',
    getCurrentEntityIds,
}: UseFeatureLayerCleanupProps<T>) {
    useEffect(() => {
        const cleanupStaleFeatures = async () => {
            if (!layer) return;

            try {
                const allFeatures = await layer.queryFeatures();

                if (cleanupStrategy === 'reset-all') {
                    const currentEntityIds = getCurrentEntityIds?.(currentItems) || [];
                    const hasMultipleFeatures = allFeatures.features.length > 1;
                    const hasStaleFeatures =
                        allFeatures.features.length > 0 &&
                        currentEntityIds.length > 0 &&
                        !allFeatures.features.some(
                            (f) =>
                                f.attributes?.entityId &&
                                currentEntityIds.includes(f.attributes.entityId)
                        );
                    const shouldCleanup = hasMultipleFeatures || hasStaleFeatures;

                    if (shouldCleanup) {
                        logFeatureLayerWarning(
                            cacheKey,
                            `running reset-all cleanup; totalFeatures=${allFeatures.features.length}, currentEntityIds=${JSON.stringify(
                            currentEntityIds
                            )}`
                        );

                        await layer.applyEdits({ deleteFeatures: allFeatures.features });
                        entityIdToObjectIdMapCacheRef.current.reset();
                    }
                } else if (cleanupStrategy === 'filter-stale' && getCurrentEntityIds) {
                    const currentEntityIds = getCurrentEntityIds(currentItems);
                    const staleFeatures = allFeatures.features.filter(
                        (f) =>
                            f.attributes?.entityId &&
                            !currentEntityIds.includes(f.attributes.entityId)
                    );

                    if (staleFeatures.length > 0) {
                        logFeatureLayerWarning(
                            cacheKey,
                            `deleting ${staleFeatures.length} stale feature(s) with filter-stale`
                        );

                        await layer.applyEdits({ deleteFeatures: staleFeatures });

                        staleFeatures.forEach((f) => {
                            if (f.attributes?.entityId) {
                                entityIdToObjectIdMapCacheRef.current.delete(f.attributes.entityId);
                            }
                        });
                    }
                }
            } catch (error) {
                logFeatureLayerWarning(
                    cacheKey,
                    `Error in feature layer cleanup: ${
                    (error as Error)?.message ?? String(error)
                    }`
                );
            }
        };

        cleanupStaleFeatures();
    }, [
        layer,
        currentItems,
        cleanupStrategy,
        cacheKey,
        entityIdToObjectIdMapCacheRef,
        getCurrentEntityIds,
    ]);

    useEffect(() => {
        return () => {
            if (layer && webMap) {
                webMap.layers.remove(layer);
                clearEntityCache(cacheKey as any);
            }
        };
    }, [layer, webMap, cacheKey]);
}
